/** MFA enrollment wizard. Used by both new invitees (with X-Enrollment-Token)
 * and logged-in users (with a session CPToken). The header used depends on
 * what's in router state — either way, the api wrapper handles it.
 *
 * Where it goes when done comes from `?returnTo=` (LoginPage forwards its
 * destination when it forces enrolment; the MCP sign-in handoff sends users
 * here and expects them back) — the home page when there is none.
 */
import {
  Alert,
  Box,
  Button,
  Container,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as inviteApi from '@/api/invite';
import * as mfaApi from '@/api/mfa';

import { useAuth } from './AuthProvider';
import { resolveLoginDestination, withReturnTo, type AuthRedirectState } from './returnTo';

interface RouterState extends AuthRedirectState {
  enrollmentToken?: string;
}

const STEPS = ['Choose method', 'Enroll', 'Confirm'];

type Method = 'totp' | 'sms' | 'email';

export function MFAEnrollPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = (location.state ?? {}) as RouterState;
  const enrollmentToken = state.enrollmentToken;
  const dest = resolveLoginDestination(searchParams, state);

  const [step, setStep] = useState(0);
  const [method, setMethod] = useState<Method>('totp');
  const [deviceId, setDeviceId] = useState<number | null>(null);
  const [target, setTarget] = useState(''); // phone or email input
  const [code, setCode] = useState('');
  const [enrollData, setEnrollData] = useState<{
    secret?: string;
    provisioning_uri?: string;
    qr?: string;
    sent_to_masked?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function startEnrollment() {
    setBusy(true);
    setError(null);
    try {
      if (method === 'totp') {
        const r = await mfaApi.enrollTotp(enrollmentToken);
        setDeviceId(r.device_id);
        setEnrollData({ secret: r.secret, provisioning_uri: r.provisioning_uri, qr: r.qr });
      } else if (method === 'sms') {
        const r = await mfaApi.enrollSms(target, enrollmentToken);
        setDeviceId(r.device_id);
        setEnrollData({ sent_to_masked: r.sent_to_masked });
      } else {
        const r = await mfaApi.enrollEmail(target, enrollmentToken);
        setDeviceId(r.device_id);
        setEnrollData({ sent_to_masked: r.sent_to_masked });
      }
      setStep(2);
    } catch (e) {
      setError(e instanceof ConsoleApiError ? e.message : 'Enrollment failed.');
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (deviceId == null) return;
    setBusy(true);
    setError(null);
    try {
      if (method === 'totp') {
        await mfaApi.confirmTotp(deviceId, code, enrollmentToken);
      } else if (method === 'sms') {
        await mfaApi.confirmSms(deviceId, code, enrollmentToken);
      } else {
        await mfaApi.confirmEmail(deviceId, code, enrollmentToken);
      }

      if (enrollmentToken) {
        // Post-invite path: finalize → session token → /
        const r = await inviteApi.finalize(enrollmentToken);
        await auth.acceptSession(r.token, r.token_expires);
        navigate(withReturnTo('/auth/backup-codes', dest), { replace: true });
        return;
      }
      // Logged-in path: refresh /me/ and go where the user was headed.
      await auth.refreshMe();
      navigate(dest, { replace: true });
    } catch (e) {
      setError(e instanceof ConsoleApiError ? e.message : 'Confirmation failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 8 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Set up two-factor authentication
      </Typography>
      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 0 && (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Pick your preferred second factor.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            {(['totp', 'sms', 'email'] as Method[]).map((m) => (
              <Button
                key={m}
                variant={method === m ? 'contained' : 'outlined'}
                onClick={() => setMethod(m)}
              >
                {m.toUpperCase()}
              </Button>
            ))}
          </Box>
          <Button variant="contained" onClick={() => setStep(1)}>
            Continue
          </Button>
        </Box>
      )}

      {step === 1 && (
        <Box>
          {method === 'totp' ? (
            <Typography sx={{ mb: 2 }}>
              We'll generate a secret. Scan it with your authenticator app on the next screen.
            </Typography>
          ) : (
            <TextField
              fullWidth
              label={method === 'sms' ? 'Mobile phone (E.164, e.g. +12025551234)' : 'Email address'}
              value={target}
              onChange={(e) => setTarget(e.target.value.trim())}
              autoFocus
              sx={{ mb: 2 }}
            />
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Button
            variant="contained"
            onClick={startEnrollment}
            disabled={busy || (method !== 'totp' && target.length === 0)}
          >
            Start enrollment
          </Button>
        </Box>
      )}

      {step === 2 && enrollData && (
        <Box>
          {method === 'totp' && enrollData.qr && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <img
                src={`data:image/svg+xml;base64,${enrollData.qr}`}
                alt="TOTP QR code"
                style={{ width: 220, height: 220 }}
              />
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Or enter this secret manually: <code>{enrollData.secret}</code>
              </Typography>
            </Box>
          )}
          {method !== 'totp' && enrollData.sent_to_masked && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Code sent to {enrollData.sent_to_masked}.
            </Alert>
          )}
          <TextField
            fullWidth
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value.trim())}
            autoFocus
            sx={{ mb: 2 }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Button variant="contained" onClick={confirm} disabled={busy || code.length === 0}>
            Confirm
          </Button>
        </Box>
      )}
    </Container>
  );
}
